/**
 * Organizer picker for sessions.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { SlidePanel } from '@/components/common/SlidePanel';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { CreateVolunteerView } from '@/components/volunteers/CreateVolunteerView';
import { cn } from '@/lib/utils';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import {
  SESSION_ORGANIZER_GROUP_OPTIONS,
  type SessionOrganizerGroupType,
} from '@/components/sessions/session-configs';
import type { UserWithProfile } from '@/types/user.types';

/** Props for {@link TutoringSessionOrganizerPickerModal}. */
interface TutoringSessionOrganizerPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrganizerCreated: (created: UserWithProfile) => void;
  onRequestCreateTutor?: () => void;
}

/** Organizer picker for sessions. */
export const TutoringSessionOrganizerPickerModal: React.FC<
  TutoringSessionOrganizerPickerModalProps
> = ({ open, onOpenChange, onOrganizerCreated, onRequestCreateTutor }) => {
  const queryClient = useQueryClient();
  const [selectedGroup, setSelectedGroup] = React.useState<SessionOrganizerGroupType | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = React.useState(false);
  const [isFormPanelOpen, setIsFormPanelOpen] = React.useState(false);
  /** Guard so opening the picker only mounts the group modal once per `open` cycle. */
  const hasStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      hasStartedRef.current = false;
      return;
    }
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    setIsGroupModalOpen(true);
  }, [open]);

  /** Tutors are created on the parent page; volunteers open a slide panel. */
  const handleGroupSelect = (group: SessionOrganizerGroupType) => {
    if (group === 'tutor' && onRequestCreateTutor) {
      onRequestCreateTutor();
      setIsGroupModalOpen(false);
      onOpenChange(false);
      return;
    }
    setSelectedGroup(group);
    setIsGroupModalOpen(false);
    setIsFormPanelOpen(true);
  };

  /** Closing the type picker also closes the parent-controlled `open`. */
  const handleGroupModalClose = () => {
    setIsGroupModalOpen(false);
    onOpenChange(false);
  };

  /** Tear down the create panel and notify the parent. */
  const handleFormClose = () => {
    setIsFormPanelOpen(false);
    setSelectedGroup(null);
    onOpenChange(false);
  };

  /** Seed the users-search cache so the new organizer appears immediately. */
  const handleCreated = (created: UserWithProfile) => {
    prependUserToUsersSearchCaches(queryClient, 'tutor', created);
    onOrganizerCreated(created);
    handleFormClose();
  };

  /** Inline create view for the selected group (`tutor` is handled by the parent). */
  const renderForm = (): React.ReactNode => {
    if (selectedGroup === 'volunteer') {
      return (
        <CreateVolunteerView
          key="volunteer-group"
          onClose={handleFormClose}
          onSuccess={handleCreated}
        />
      );
    }
    return null;
  };

  return (
    <>
      <BaseModal
        open={isGroupModalOpen}
        onClose={handleGroupModalClose}
        size="md"
        header={{
          icon: UserPlus,
          iconSize: 'md',
          title: 'Créer un bénévole',
          subtitle: 'Ajouter un volontaire, un bénévole ou un professeur',
          titleSize: 'lg',
          titleWeight: 'semibold',
        }}
        hideContentWrapper
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {SESSION_ORGANIZER_GROUP_OPTIONS.map((group) => {
            const Icon = group.icon;
            return (
              <button
                key={group.type}
                type="button"
                onClick={() => handleGroupSelect(group.type)}
                className={cn(
                  'group relative flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all',
                  'hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2',
                )}
                style={{
                  borderColor: 'var(--color-1-nuance-3)',
                  background: 'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = group.accentColor;
                  e.currentTarget.style.background = `color-mix(in srgb, ${group.accentColor} 8%, transparent)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-1-nuance-3)';
                  e.currentTarget.style.background =
                    'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)';
                }}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl border-2 transition-all"
                  style={{
                    borderColor: group.accentColor,
                    background: `color-mix(in srgb, ${group.accentColor} 10%, transparent)`,
                  }}
                >
                  <Icon className="h-6 w-6" style={{ color: group.accentColor }} />
                </div>
                <div>
                  <h3
                    className="text-base font-semibold"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  >
                    {group.label}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{group.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </BaseModal>

      <SlidePanel isOpen={isFormPanelOpen} onClose={handleFormClose}>
        {renderForm()}
      </SlidePanel>
    </>
  );
};
